import {
  Box,
  Checkbox,
  Flex,
  InputDescription,
  OptionType,
  SelectMenu,
  Spinner,
  Text,
  TextInput,
  Tooltip,
  Pill,
  Pane,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { ResourceSelectMenu } from '@fullstory/library-ui';
import { MultiPlatformSelectorInputs, SelectorInputSource } from '../MultiPlatformSelectorInputs';
import {
  NamedElementModal,
  NamedElementModalSource,
  NamedElementModalVariant,
} from '../NamedElementModal';
import { NamedElementSelectMenu } from '../NamedElementSelectMenu';
import { NativePlatform, PlatformSelector } from '@fullstory/proto/fs/api/segment';
import { ValidateNamedElementResponse } from '@fullstory/proto/fs/services/dx-objects';
import {
  ElementPropertyRule,
  NamedElementRecord,
} from '@fullstory/proto/fs/services/search-settings';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { useFlags } from '@fullstory/react-hooks';
import {
  elementPropertyRulesSelectors,
  useNamedElementActions,
  useFetchFsSpec,
} from '@fullstory/redux';
import { ElementValueType } from '@fullstory/search-types';
import { SelectorRule } from '@fullstory/selectors';
import { assertExhaustive } from '@fullstory/typeutils';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { getSampleValuesForOneof, placeholderNamedElementRecord } from '../../utils';
import { useNamedElements } from '../../hooks';
import { ElementPropertyRuleErrors, ElementPropertyRuleTouched } from './index';
import { ActiveIndexContext } from '@fullstory/signal-studio-context';
import {
  AvailableRawValues,
  ElementSelectorsConfig,
  SampleValues,
  SingleSampleValues,
} from '../../types';
import classNames from 'classnames';
import css from './index.module.scss';
import { compileRegexValueFormat } from '../../utils/regex';
import { isEqual, uniq, uniqWith } from 'lodash';

const { getUnarchivedElementPropertyRulesByName } = elementPropertyRulesSelectors;

const elementOptionElement: OptionType<ElementPropertyRule.element> = {
  key: 'element',
  value: ElementPropertyRule.element.namedElementId,
  label: intl.formatMessage({ defaultMessage: 'Element', id: 'SHkKGm' }),
};

const elementOptions: OptionType<ElementPropertyRule.element>[] = [
  {
    key: 'any',
    value: ElementPropertyRule.element.any,
    label: intl.formatMessage({ defaultMessage: 'Any', id: 'ToO3/h' }),
  },
  elementOptionElement,
  {
    key: 'selector',
    value: ElementPropertyRule.element.selectors,
    label: intl.formatMessage({ defaultMessage: 'Selector', id: 'gxkld2' }),
  },
];

export interface Element {
  any?: boolean;
  namedElementId?: string;
  selectors?: ElementSelectorsConfig | null;
}

export type ElementErrors = Pick<ElementPropertyRuleErrors, 'any' | 'namedElementId' | 'selectors'>;
export type ElementTouched = Pick<
  ElementPropertyRuleTouched,
  'any' | 'namedElementId' | 'selectors'
>;

export interface ElementPropertyRuleElementProps {
  element: Element;
  onChange: (element: Element) => void;
  touched?: ElementTouched;
  setTouched?: (touched: ElementTouched) => void;
  errors: ElementErrors;
  disabled?: boolean;
  promotedElements?: string[];
  omitNonPromotedElements?: boolean;
  smallDropdowns?: boolean;
  trimTrigger?: boolean;
  setTrimTrigger?: (trim: boolean) => void;
  preventOverflow?: boolean;
}

export const ElementPropertyRuleElement: React.FC<ElementPropertyRuleElementProps> = ({
  element,
  onChange,
  touched,
  setTouched,
  errors,
  promotedElements,
  omitNonPromotedElements,
  disabled,
  smallDropdowns,
  trimTrigger,
  setTrimTrigger,
  preventOverflow,
}) => {
  const flags = useFlags();
  const webOnly = !flags.hasBit(Flag.FeatNativeMobile);
  const defaultPlatforms = webOnly ? [NativePlatform.NATIVE_PLATFORM_WEB] : [];
  const activeIndex = useContext(ActiveIndexContext);

  const selected = element.any
    ? ElementPropertyRule.element.any
    : element.selectors
    ? ElementPropertyRule.element.selectors
    : ElementPropertyRule.element.namedElementId;
  const selectedElement =
    elementOptions.find(opt => opt.value === selected) ?? elementOptionElement;
  const selectorValidationStates = useMemo(
    (): ValidateNamedElementResponse.ValidationState[] =>
      element.selectors
        ? element.selectors.selectors.map(sel => {
            const error = validateSelector(sel);
            if (error !== undefined) {
              return { invalid: { message: error } };
            }
            return { valid: {} };
          })
        : [],
    [element.selectors],
  );

  useFetchFsSpec();
  const { isLoading } = useNamedElements();
  const {
    save: saveNamedElement,
    archive: archiveNamedElement,
    restore: restoreNamedElement,
  } = useNamedElementActions();

  const [modalInput, setModalInput] = React.useState<NamedElementRecord | null>(null);

  return (
    <Box data-component="ElementPropertyRuleElement">
      <Flex
        justifyContent="flex-start"
        alignItems="baseline"
        data-component="ElementPropertyRuleElement"
        gap={8}
      >
        <Pane
          className={classNames(css.elementSelectMenu, {
            [css.active]:
              selectedElement.value === ElementPropertyRule.element.any && activeIndex?.index === 0,
          })}
        >
          <SelectMenu
            options={elementOptions}
            selected={selectedElement}
            variant={smallDropdowns ? 'fill-builder' : undefined}
            onChange={opt => {
              if (opt) {
                switch (opt.value) {
                  case ElementPropertyRule.element.any:
                    onChange({ any: true });
                    break;
                  case ElementPropertyRule.element.namedElementId:
                    onChange({ namedElementId: '' });
                    break;
                  case ElementPropertyRule.element.selectors:
                    onChange({
                      selectors: {
                        selectors: [
                          {
                            selector: '',
                            originalSelector: '',
                            canonicalizedSelector: null,
                            platforms: [...defaultPlatforms],
                          },
                        ],
                      },
                    });
                    break;
                  default:
                    assertExhaustive(opt.value);
                }
                activeIndex?.setIndex(0);
              }
            }}
            disabled={disabled}
            flipY={preventOverflow || undefined}
            preventOverflow={preventOverflow ? 16 : undefined}
          />
        </Pane>
        {selectedElement.value !== ElementPropertyRule.element.any && (
          <Text appearance="bodyMedium" color="secondary">
            <FormattedMessage defaultMessage="is" id="fXdkiI" />
          </Text>
        )}
        {selectedElement.value === ElementPropertyRule.element.namedElementId && (
          <>
            {isLoading ? (
              <Box alignSelf="center">
                <Spinner />
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" collapsible>
                <ElementSelectMenu
                  namedElementId={element.namedElementId}
                  onChange={namedElementId => {
                    onChange({ namedElementId });
                    activeIndex?.setIndex(0);
                  }}
                  onCreate={() => setModalInput(placeholderNamedElementRecord())}
                  setTouched={t => setTouched?.({ ...touched, namedElementId: t })}
                  active={activeIndex?.index === 0} // when element is the selection, there is only one index
                  disabled={disabled}
                  promotedElements={promotedElements}
                  omitNonPromotedElements={omitNonPromotedElements}
                  smallDropdowns={smallDropdowns}
                  preventOverflow={preventOverflow}
                />
                {(!touched || touched.namedElementId) &&
                  errors.namedElementId &&
                  !modalInput?.namedElement && (
                    <InputDescription invalid description={errors.namedElementId} />
                  )}
              </Box>
            )}
            {modalInput?.namedElement && (
              <NamedElementModal
                initialElement={modalInput.namedElement}
                onCloseSignal={() => setModalInput(null)}
                variant={NamedElementModalVariant.new}
                onSave={(
                  namedElement,
                  captureOptions,
                  silence,
                  fsSpecSlugs,
                  hasExistingIssue,
                  classification,
                ) => {
                  saveNamedElement(
                    namedElement,
                    captureOptions,
                    silence,
                    fsSpecSlugs,
                    hasExistingIssue,
                    classification,
                  ).then(rsp => {
                    if (rsp?.namedElement?.namedElement?.namedElement?.id) {
                      onChange({ namedElementId: rsp.namedElement.namedElement.namedElement.id });
                    }
                  });
                }}
                onArchive={archiveNamedElement}
                onUnarchive={restoreNamedElement}
                source={NamedElementModalSource.relatedSettings}
              />
            )}
          </>
        )}
      </Flex>
      {selectedElement.value === ElementPropertyRule.element.selectors && (
        <Box marginTop={8}>
          <MultiPlatformSelectorInputs
            selectors={
              element.selectors?.selectors ?? [
                {
                  selector: '',
                  originalSelector: '',
                  canonicalizedSelector: null,
                  platforms: [...defaultPlatforms],
                },
              ]
            }
            onChange={selectors => onChange({ selectors: { selectors } })}
            onHoverSelector={() => {}}
            validationStates={
              (!touched || touched.selectors) && errors.selectors !== undefined
                ? selectorValidationStates
                : []
            }
            disabled={disabled}
            // api elements not included in property extraction rules
            apiName=""
            savedApiName=""
            source={SelectorInputSource.propertyModal}
            trimTrigger={trimTrigger}
            setTrimTrigger={setTrimTrigger}
          />
        </Box>
      )}
    </Box>
  );
};

interface ElementSelectMenuProps {
  namedElementId?: string;
  onChange: (namedElementId?: string) => void;
  onCreate: () => void;
  setTouched?: (touched: boolean) => void;
  active?: boolean;
  disabled?: boolean;
  promotedElements?: string[];
  omitNonPromotedElements?: boolean;
  smallDropdowns?: boolean;
  preventOverflow?: boolean;
}

const ElementSelectMenu: React.FC<ElementSelectMenuProps> = ({
  namedElementId,
  onChange,
  onCreate,
  setTouched,
  active,
  disabled,
  promotedElements,
  omitNonPromotedElements,
  smallDropdowns,
  preventOverflow,
}) => {
  const namedElement = useMemo(
    () => (namedElementId ? { type: ElementValueType.Id, value: namedElementId } : null),
    [namedElementId],
  );
  const promoted = useMemo(
    () =>
      promotedElements
        ? {
            group: intl.formatMessage({ defaultMessage: 'Shown in preview', id: 'Purap7' }),
            ids: new Set<string>(promotedElements),
          }
        : undefined,
    [promotedElements],
  );

  return (
    <Pane
      data-component="ElementSelectMenu"
      className={classNames(css.elementSelectMenu, { [css.active]: active })}
      display="flex"
      flexDirection="column"
    >
      <NamedElementSelectMenu
        namedElement={namedElement}
        setNamedElement={elementValue => {
          const namedElementId =
            elementValue?.type === ElementValueType.Id ? elementValue.value : '';
          onChange(namedElementId);
        }}
        onClose={() => {
          // need to defer setting touched a cycle due to timing around closing and setting the named element
          setTimeout(() => setTouched?.(true), 0);
        }}
        onCreate={onCreate}
        source={NamedElementModalSource.relatedSettings}
        omitSpecElements
        clearable
        disabled={disabled}
        promote={promoted}
        omitNonPromoted={omitNonPromotedElements}
        variant={smallDropdowns ? 'fill-builder' : undefined}
        preventOverflow={preventOverflow ? 16 : undefined}
        flipY={preventOverflow || undefined}
      />
    </Pane>
  );
};

const validateSelector = (sel: PlatformSelector): string | undefined => {
  if (!sel.selector) {
    return '';
  }
  if (!SelectorRule.isValid(sel.selector)) {
    return intl.formatMessage({
      defaultMessage: 'That selector has invalid syntax.',
      id: 'b6GUuB',
    });
  }
  return undefined;
};

const sourceOptionAttribute: OptionType<ElementPropertyRule.source> = {
  key: 'attribute',
  value: ElementPropertyRule.source.attribute,
  label: intl.formatMessage({ defaultMessage: 'Attribute', id: 'OsU2Fs' }),
};

const allSourceOptions: OptionType<ElementPropertyRule.source>[] = [
  sourceOptionAttribute,
  {
    key: 'innerText',
    value: ElementPropertyRule.source.innerText,
    label: intl.formatMessage({ defaultMessage: 'Text', id: 'aA8bDw' }),
  },
  {
    key: 'static',
    value: ElementPropertyRule.source.static,
    label: intl.formatMessage({ defaultMessage: 'Static', id: 'fCu/+n' }),
  },
];

interface Source {
  attribute?: ElementPropertyRule.SourceAttribute | null;
  innerText?: ElementPropertyRule.SourceInnerText | null;
  static?: ElementPropertyRule.SourceStatic | null;
}

export type SourceErrors = Pick<ElementPropertyRuleErrors, 'attribute' | 'innerText' | 'static'>;
export type SourceTouched = Pick<ElementPropertyRuleTouched, 'attribute' | 'innerText' | 'static'>;

interface ElementPropertyRuleSourceProps {
  labeledBy: string;
  availableValues?: AvailableRawValues;
  availableValuesLoading?: boolean;
  source: Source;
  onChange: (source: Source) => void;
  touched?: SourceTouched;
  setTouched?: (touched: SourceTouched) => void;
  errors: SourceErrors;
  valueRegex: boolean;
  setValueRegex: (valueRegex: boolean) => void;
  disabledOptions?: boolean;
  disabled?: boolean;
  smallDropdowns?: boolean;
  preventOverflow?: boolean;
}

export const ElementPropertyRuleSource: React.FC<ElementPropertyRuleSourceProps> = ({
  labeledBy,
  availableValues,
  availableValuesLoading,
  source,
  onChange,
  touched,
  setTouched,
  errors,
  valueRegex,
  setValueRegex,
  disabledOptions,
  disabled,
  smallDropdowns,
  preventOverflow,
}) => {
  const flags = useFlags();

  const selected = source.innerText
    ? ElementPropertyRule.source.innerText
    : source.static
    ? ElementPropertyRule.source.static
    : ElementPropertyRule.source.attribute;
  const selectedSource =
    allSourceOptions.find(opt => opt.value === selected) ?? sourceOptionAttribute;

  // only show "extract with regex":
  // 1. when enabled
  // 2. when not static -- static values fully control the value so using regex doesn't make sense
  // 3. when not loading with attribute selected
  const regexValueEnabled =
    flags.featureFlags().AppElementPropertiesRegexValue &&
    selected !== ElementPropertyRule.source.static &&
    (selected !== ElementPropertyRule.source.attribute || !availableValuesLoading);

  const [attributeInitialOpen, setAttributeInitialOpen] = useState(false);
  const availableAttributeKeys: SingleSampleValues<string> | undefined = useMemo(() => {
    if (!availableValues) return undefined;
    const availableAttributes = uniq(
      availableValues.values.flatMap(values => Object.keys(values.attributes)),
    );
    return {
      type: 'single',
      values: availableAttributes.sort((a, b) => a.localeCompare(b)),
    };
  }, [availableValues]);
  const getAttributeValues = useCallback(
    (attr: string): SampleValues<string> => {
      if (!availableValues) {
        return { type: 'single', values: [] };
      }
      return getSampleValuesForOneof(
        {
          opt: ElementPropertyRule.source.attribute,
          val: { attribute: attr, includeAncestors: false },
        },
        availableValues,
      );
    },
    [availableValues],
  );
  const availableAttributeOptions = useMemo((): OptionType<string>[] | undefined => {
    if (!availableAttributeKeys) return undefined;
    const keysForOptions = [...availableAttributeKeys.values];
    if (
      source.attribute?.attribute &&
      !keysForOptions.some(key => key === source.attribute?.attribute)
    ) {
      keysForOptions.push(source.attribute.attribute);
    }
    const options: OptionType<string>[] = keysForOptions.map(attr => ({
      key: attr,
      value: attr,
      label: attr,
    }));
    if (options.length && availableValuesLoading) {
      // add a synthetic option to show a loading spinner
      // a bit of a hack, but we can use the rich text label to show the spinner
      options.push({
        key: '',
        value: '',
        label: intl.formatMessage({ defaultMessage: 'Loading...', id: 'gjBiyj' }),
        richTextLabel: <Spinner />,
        disabled: true,
      });
    }
    return options;
  }, [availableAttributeKeys, availableValuesLoading, source.attribute?.attribute]);

  const availableText: SampleValues<string> | undefined = useMemo(() => {
    if (!availableValues) return undefined;
    return getSampleValuesForOneof(
      { opt: ElementPropertyRule.source.innerText, val: {} },
      availableValues,
    );
  }, [availableValues]);

  const hasAttributesAvailable = !!availableAttributeKeys?.values?.length;
  const isAttributeSelected = selectedSource.value === ElementPropertyRule.source.attribute;
  const includeAttributeOption =
    availableValuesLoading || hasAttributesAvailable || isAttributeSelected;
  const sourceOptions = useMemo(
    () =>
      includeAttributeOption
        ? allSourceOptions
        : allSourceOptions.filter(opt => opt.value !== ElementPropertyRule.source.attribute),
    [allSourceOptions, includeAttributeOption],
  );

  const renderSourceDefinition = (opt: OptionType<ElementPropertyRule.source>) => {
    switch (opt.value) {
      case ElementPropertyRule.source.attribute:
        return (
          <SelectFlyout
            definition={intl.formatMessage({
              defaultMessage:
                'Attributes are found in the structure of your website or app code. Use attributes when there are dynamic values coded in the selector that you want to store as properties.',
              id: '2YmzMq',
            })}
            availableValues={
              availableAttributeKeys
                ? {
                    title: intl.formatMessage({
                      defaultMessage: 'Available attributes',
                      id: 'GoH2rh',
                    }),
                    values: availableAttributeKeys,
                    valuesLoading: availableValuesLoading,
                    emptyMessage: intl.formatMessage({
                      defaultMessage: 'No attributes available',
                      id: 'L2GwCQ',
                    }),
                  }
                : undefined
            }
          />
        );
      case ElementPropertyRule.source.innerText:
        return (
          <SelectFlyout
            definition={intl.formatMessage({
              defaultMessage:
                'Text will store the text of the lowest leaf element in your selector as the property.',
              id: 'JgXyDi',
            })}
            availableValues={
              availableText
                ? {
                    title: intl.formatMessage({ defaultMessage: 'Sample values', id: 'bqUvep' }),
                    values: availableText,
                    valuesLoading: availableValuesLoading,
                    emptyMessage: intl.formatMessage({
                      defaultMessage: 'No sample values available',
                      id: 'WzgXgS',
                    }),
                  }
                : undefined
            }
          />
        );
      case ElementPropertyRule.source.static:
        return (
          <SelectFlyout
            definition={intl.formatMessage({
              defaultMessage: 'Static allows you to set the property value directly.',
              id: '621XuL',
            })}
          />
        );
      default:
        assertExhaustive(opt.value);
    }
  };

  return (
    <>
      <Flex
        justifyContent="flex-start"
        alignItems="baseline"
        data-component="ElementPropertyRuleSource"
        gap={8}
      >
        <Box flexShrink={0}>
          <ResourceSelectMenu<ElementPropertyRule.source>
            itemType="propertyRuleSource"
            options={sourceOptions}
            selected={selectedSource}
            onChange={opt => {
              if (opt) {
                switch (opt.value) {
                  case ElementPropertyRule.source.attribute:
                    setAttributeInitialOpen(true);
                    onChange({
                      attribute: { attribute: '', includeAncestors: false, ...source.attribute },
                    });
                    break;
                  case ElementPropertyRule.source.innerText:
                    onChange({ innerText: { ...source.innerText } });
                    break;
                  case ElementPropertyRule.source.static:
                    onChange({ static: { value: '', ...source.static } });
                    break;
                  default:
                    assertExhaustive(opt.value);
                }
              }
            }}
            disabled={disabledOptions || disabled}
            renderDefinition={renderSourceDefinition}
            variant={smallDropdowns ? 'fill-builder' : undefined}
            preventOverflow={preventOverflow ? 16 : undefined}
            flipY={preventOverflow || undefined}
          />
        </Box>
        {selectedSource.value !== ElementPropertyRule.source.innerText && (
          <Text appearance="bodyMedium" color="secondary">
            <FormattedMessage defaultMessage="is" id="fXdkiI" />
          </Text>
        )}
        {selectedSource.value === ElementPropertyRule.source.attribute ? (
          availableAttributeOptions ? (
            availableValuesLoading && !source.attribute?.attribute ? (
              <Box alignSelf="center">
                <Spinner />
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" collapsible>
                <ResourceSelectMenu<string>
                  itemType="attribute"
                  options={availableAttributeOptions}
                  variant={smallDropdowns ? 'fill-builder' : undefined}
                  selected={availableAttributeOptions.find(
                    opt => opt.value === source.attribute?.attribute,
                  )}
                  onChange={opt => {
                    onChange({
                      attribute: {
                        attribute: opt?.value ?? '',
                        includeAncestors: !!source.attribute?.includeAncestors,
                      },
                    });
                  }}
                  onClose={() => {
                    setAttributeInitialOpen(false);
                    // need to defer setting touched a cycle due to timing around closing and setting the attr
                    setTimeout(() => setTouched?.({ ...touched, attribute: true }), 0);
                  }}
                  initialOpen={attributeInitialOpen && !!availableAttributeOptions.length}
                  clearable
                  disabled={disabled || !availableAttributeOptions.length}
                  placeholder={
                    !availableAttributeOptions.length
                      ? intl.formatMessage({
                          defaultMessage: 'No attributes available',
                          id: 'L2GwCQ',
                        })
                      : undefined
                  }
                  renderDefinition={opt => (
                    <SelectFlyout
                      availableValues={{
                        title: intl.formatMessage({
                          defaultMessage: 'Sample values',
                          id: 'bqUvep',
                        }),
                        values: getAttributeValues(opt.value),
                        valuesLoading: availableValuesLoading,
                        emptyMessage: intl.formatMessage({
                          defaultMessage: 'No sample values available',
                          id: 'WzgXgS',
                        }),
                      }}
                    />
                  )}
                  preventOverflow={preventOverflow ? 16 : undefined}
                  flipY={preventOverflow || undefined}
                />
                {(!touched || touched.attribute) && errors.attribute && (
                  <InputDescription invalid description={errors.attribute} />
                )}
              </Box>
            )
          ) : (
            <Box flexGrow={1}>
              <TextInput
                onChange={e => {
                  onChange({
                    attribute: {
                      attribute: e.target.value,
                      includeAncestors: !!source.attribute?.includeAncestors,
                    },
                  });
                }}
                onBlur={() => setTouched?.({ ...touched, attribute: true })}
                value={source.attribute?.attribute ?? ''}
                placeholder={intl.formatMessage({
                  defaultMessage: 'e.g. data-product-name',
                  id: 'P9HmZX',
                })}
                invalid={(!touched || touched.attribute) && errors.attribute !== undefined}
                aria-labelledby={labeledBy}
                testId="element-property-rule-modal-source-attribute-input"
                disabled={disabled}
              />
            </Box>
          )
        ) : null}
        {selectedSource.value === ElementPropertyRule.source.static && (
          <Box flexGrow={1}>
            <TextInput
              onChange={e => {
                onChange({ static: { value: e.target.value } });
              }}
              onBlur={() => setTouched?.({ ...touched, static: true })}
              value={source.static?.value ?? ''}
              placeholder={intl.formatMessage({
                defaultMessage: 'e.g. T-Shirt',
                id: 'O3Mfy/',
              })}
              invalid={(!touched || touched.static) && errors.static !== undefined}
              aria-labelledby={labeledBy}
              testId="element-property-rule-modal-source-static-input"
              disabled={disabled}
            />
          </Box>
        )}
        {regexValueEnabled && (
          <Box flexShrink={0}>
            <Checkbox
              checked={valueRegex}
              onChange={setValueRegex}
              label={intl.formatMessage({
                defaultMessage: 'Extract with regex',
                id: 'CeeW54',
              })}
              disabled={disabled}
            />
          </Box>
        )}
      </Flex>
    </>
  );
};

interface SelectFlyoutProps {
  definition?: string;
  availableValues?: AvailableValuesProps;
}

const SelectFlyout: React.FC<SelectFlyoutProps> = ({ definition, availableValues }) => (
  <Flex data-component="SelectFlyout" column alignItems="stretch" gap={8}>
    {definition && (
      <>
        <Box>
          <Text appearance="bodySmall" color="secondary">
            <FormattedMessage defaultMessage="Definition" id="KbSPMS" />
          </Text>
        </Box>
        <Box>
          <Text appearance="bodyMedium">{definition}</Text>
        </Box>
      </>
    )}
    {availableValues && <AvailableValues {...availableValues} />}
  </Flex>
);

export interface AvailableValuesProps {
  title: string;
  values: SampleValues<string>;
  valuesLoading?: boolean;
  emptyMessage: string;
}

export const AvailableValues: React.FC<AvailableValuesProps> = ({
  title,
  values: _values,
  valuesLoading,
  emptyMessage,
}) => {
  const values: SampleValues<string> = useMemo(
    () =>
      _values.type === 'single'
        ? { type: 'single', values: uniq(_values.values) }
        : { type: 'multi', values: uniqWith(_values.values, isEqual) },
    [_values],
  );

  return (
    <Box data-component="AvailableValues" display="flex" flexDirection="column" gap={8}>
      <Box>
        <Text appearance="bodySmall" color="secondary">
          {title}
        </Text>
      </Box>
      {valuesLoading ? (
        <Spinner />
      ) : values.type === 'single' ? (
        <AvailableValuesPills values={values.values} emptyMessage={emptyMessage} />
      ) : values.values.length ? (
        values.values.map((values, idx) => (
          <Pane
            key={idx}
            backgroundColor="secondary"
            border="standard"
            borderRadius={4}
            padding={8}
          >
            <AvailableValuesPills values={values} emptyMessage={emptyMessage} />
          </Pane>
        ))
      ) : (
        <AvailableValuesPills values={[]} emptyMessage={emptyMessage} />
      )}
    </Box>
  );
};

export interface AvailableValuesPillsProps {
  values: string[];
  emptyMessage: string;
}

const AvailableValuesPills: React.FC<AvailableValuesPillsProps> = ({ values, emptyMessage }) => (
  <Flex
    data-component="AvailableValuesPills"
    alignItems="flex-start"
    justifyContent="flex-start"
    flexWrap="wrap"
    gap={4}
  >
    {values.length ? (
      values.map((value, idx) => (
        <Tooltip
          key={idx}
          tooltipContent={
            value ? (
              value
            ) : (
              <i>
                <FormattedMessage defaultMessage="Empty" id="+RhnH+" />
              </i>
            )
          }
          showDelay={300}
          childrenOverflowHidden
        >
          <Pill variant="primary" noExternalSpacing>
            <Text appearance="bodySmall" noWrap>
              {value ? value : <>&nbsp;</>}
            </Text>
          </Pill>
        </Tooltip>
      ))
    ) : (
      <Text appearance="bodySmall" color="disabled">
        <i>{emptyMessage}</i>
      </Text>
    )}
  </Flex>
);

export const useValidateElementPropertyRule = () => {
  const unarchivedByName = useSelector(getUnarchivedElementPropertyRulesByName);

  return useCallback(
    (values: ElementPropertyRule): ElementPropertyRuleErrors => {
      const errors: ElementPropertyRuleErrors = {};
      // name
      if (!values.name.trim()) {
        // force invalid, no message, just input invalid style
        errors.name = '';
      } else if (
        unarchivedByName.has(values.name) &&
        unarchivedByName.get(values.name)?.elementPropertyRule?.id !== values.id
      ) {
        errors.name = intl.formatMessage({
          defaultMessage: 'That name is already in use.',
          id: 'H/3qLc',
        });
      }

      // element
      const element = ElementPropertyRule.element_oneof(values);
      if (
        !element ||
        (element.opt === ElementPropertyRule.element.namedElementId && !element.val)
      ) {
        // nothing selected initially or element option selected w/o named element selected
        errors.namedElementId = intl.formatMessage({
          defaultMessage: 'An element must be selected.',
          id: 'UMHG0l',
        });
      } else if (
        element.opt === ElementPropertyRule.element.selectors &&
        element.val.selectors.some(sel => validateSelector(sel) !== undefined)
      ) {
        // force invalid, but message not actually used since underlying component needs more involved repeated validation
        errors.selectors = '';
      }

      // child element
      const child = ElementPropertyRule.child_oneof(values);
      if (child?.opt === ElementPropertyRule.child.childNamedElementId) {
        if (child.val) {
          if (values.namedElementId && child.val === values.namedElementId) {
            errors.childNamedElementId = intl.formatMessage({
              defaultMessage: 'The selected element and parent element must be different.',
              id: '127K4I',
            });
          }
        } else {
          // child element option selected w/o named element selected
          errors.childNamedElementId = intl.formatMessage({
            defaultMessage: 'An element must be selected.',
            id: 'UMHG0l',
          });
        }
      } else if (
        child?.opt === ElementPropertyRule.child.childSelectors &&
        child.val.selectors.some(sel => validateSelector(sel) !== undefined)
      ) {
        // force invalid, but message not actually used since underlying component needs more involved repeated validation
        errors.childSelectors = '';
      }

      // source
      const source = ElementPropertyRule.source_oneof(values);
      if (
        !source ||
        (source.opt === ElementPropertyRule.source.attribute && !source.val.attribute.trim())
      ) {
        // nothing selected initially or attribute option selected w/o attribute filled in
        errors.attribute = intl.formatMessage({
          defaultMessage: 'An attribute must be selected.',
          id: '4E1bA3',
        });
      } else if (source.opt === ElementPropertyRule.source.static && !source.val.value) {
        errors.static = '';
      }

      // property
      if (!values.propertyName) {
        // force invalid, no message, just input invalid style
        errors.propertyName = '';
      } else if (!values.propertyName.match(/^[a-zA-Z_][a-zA-Z0-9_-]*$/)) {
        errors.propertyName = intl.formatMessage({
          defaultMessage:
            'The property name may only contain alphanumeric characters (A-Z, a-z, 0-9), underscores (_), or hyphens (-). The property name must start with an alphabetic character (A-Z or a-z).',
          id: 'O8dzEd',
        });
      }
      if (!values.propertyType) {
        errors.propertyType = intl.formatMessage({
          defaultMessage: 'A property type must be selected.',
          id: 'LOSbG8',
        });
      }

      // regex
      if (values.regex) {
        // expression is required
        if (!values.regex.expression) {
          // force invalid, no message, just input invalid style
          if (!errors.regex) {
            errors.regex = {};
          }
          errors.regex.expression = '';
        } else {
          try {
            new RegExp(values.regex.expression);
          } catch (e) {
            if (!errors.regex) {
              errors.regex = {};
            }
            errors.regex.expression = intl.formatMessage({
              defaultMessage: 'The regex expression is invalid.',
              id: 'KzgNVs',
            });
          }
        }
        // format is optional but should be validated when provided
        if (values.regex.format) {
          const { error } = compileRegexValueFormat(values.regex.format);
          if (error) {
            if (!errors.regex) {
              errors.regex = {};
            }
            errors.regex.format = error;
          }
        }
      }

      return errors;
    },
    [unarchivedByName],
  );
};
